import React,{useState} from 'react';
import Icon from '../Text/Icon';
import {nanoid} from 'nanoid';

/**
 * ChatTabs - Manages multiple chat sessions with tabs
 */
export default function ChatTabs({tabs,activeTabId,onTabChange,onAddTab,onRemoveTab}) {
    const [isAddingTab,setIsAddingTab]=useState(false);
    const [newTabName,setNewTabName]=useState('');

    // 5.5.2 — Arrow-key navigation between tabs
    const handleTabKeyDown=(e,tabId)=>{
        const idx=tabs.findIndex(t=>t.id===tabId);
        if(e.key==='ArrowRight'||e.key==='ArrowDown'){
            e.preventDefault();
            const next=tabs[(idx+1)%tabs.length];
            onTabChange(next.id);
        } else if(e.key==='ArrowLeft'||e.key==='ArrowUp'){
            e.preventDefault();
            const prev=tabs[(idx-1+tabs.length)%tabs.length];
            onTabChange(prev.id);
        } else if(e.key==='Delete'||e.key==='Backspace'){
            if(tabs.length>1){e.preventDefault();onRemoveTab(tabId);}
        }
    };

    const handleAddTab=() => {
        if(newTabName.trim()) {
            onAddTab({
                id: nanoid(),
                name: newTabName.trim(),
                messages: [{role: 'model',content: 'Hello! How can I help with your flowchart?'}]
            });
            setNewTabName('');
            setIsAddingTab(false);
        }
    };

    const handleKeyDown=(e) => {
        if(e.key==='Enter') {
            handleAddTab();
        } else if(e.key==='Escape') {
            setIsAddingTab(false);
            setNewTabName('');
        }
    };

    return (
        <div className="flex items-center gap-2 bg-gray-50 border-b border-gray-200 w-full">
            {/* Scrollable Tabs Container */}
            <div role="tablist" aria-label="Chat sessions" className="flex items-center gap-1 px-2 py-2 overflow-x-auto flex-1 min-w-0">
                {/* Existing Tabs */}
                {tabs.map((tab) => (
                    <div
                        key={tab.id}
                        role="tab"
                        tabIndex={activeTabId===tab.id?0:-1}
                        aria-selected={activeTabId===tab.id}
                        className={`
                            flex items-center gap-2 px-3 py-1.5 rounded-t-md whitespace-nowrap cursor-pointer transition-colors flex-shrink-0
                            ${activeTabId===tab.id
                                ? 'bg-white border-t border-l border-r border-gray-300 font-semibold text-primary'
                                :'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }
                        `}
                        onClick={() => onTabChange(tab.id)}
                        onKeyDown={(e)=>handleTabKeyDown(e,tab.id)}
                    >
                        <Icon icon="chat-left-text" className="text-sm" />
                        <span className="text-sm">{tab.name}</span>

                        {/* Close button - only show if more than 1 tab */}
                        {tabs.length>1&&(
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveTab(tab.id);
                                }}
                                className="ml-1 hover:bg-gray-300 rounded p-0.5 transition-colors"
                                aria-label="Close tab"
                            >
                                <Icon icon="x" className="text-xs" />
                            </button>
                        )}
                    </div>
                ))}

                {/* Add Tab Button */}
                {isAddingTab? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-t-md border border-gray-300 flex-shrink-0">
                        <input
                            type="text"
                            value={newTabName}
                            onChange={(e) => setNewTabName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={() => {
                                if(!newTabName.trim()) {
                                    setIsAddingTab(false);
                                }
                            }}
                            placeholder="Tab name..."
                            className="text-sm outline-none w-24"
                            autoFocus
                        />
                        <button
                            onClick={handleAddTab}
                            className="hover:bg-green-100 rounded p-0.5 transition-colors"
                        >
                            <Icon icon="check" className="text-xs text-green-600" />
                        </button>
                    </div>
                ):(
                    <button
                        onClick={() => setIsAddingTab(true)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-t-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors flex-shrink-0"
                        aria-label="Add new chat tab"
                    >
                        <Icon icon="plus" className="text-sm" />
                        <span className="text-sm font-semibold">New Chat</span>
                    </button>
                )}
            </div>

            {/* Tabs Info - Fixed on Right */}
            <div className="flex items-center gap-2 text-xs text-gray-500 px-3 flex-shrink-0 border-l border-gray-200">
                <span>{tabs.length} {tabs.length===1? 'chat':'chats'}</span>
            </div>
        </div>
    );
}
